class SearchCombobox extends HTMLElement {
	constructor() {
		super();

		this.input = this.querySelector('.search-combobox__input');
		this.results = this.querySelector('.search-combobox__results');
		this.loading = this.querySelector('.search-combobox__loading');
		this.content = this.querySelector('.search-combobox__content');
		this.noResults = this.querySelector('.search-combobox__no-results');
		this.liveRegion = this.querySelector('#search-combobox-live-region');
		this.form = this.querySelector('.search-combobox__form');

		this.searchTimeout = null;
		this.currentQuery = '';
		this.selectedIndex = -1;
		this.items = [];
		this.isOpen = false;

		this.predictiveSearchEnabled = this.dataset.predictiveSearch === 'true';

		this.setupEventListeners();
	}

	setupEventListeners() {
		if (!this.predictiveSearchEnabled) return;

		this.input.addEventListener('input', this.handleInput.bind(this));
		this.input.addEventListener('keydown', this.handleKeydown.bind(this));
		this.input.addEventListener('focus', this.handleFocus.bind(this));
		this.input.addEventListener('blur', this.handleBlur.bind(this));

		// Close on click outside
		document.addEventListener('click', this.handleDocumentClick.bind(this));

		// Form submission
		this.form.addEventListener('submit', this.handleSubmit.bind(this));
	}

	handleInput(event) {
		const query = event.target.value.trim();

		if (query.length === 0) {
			this.hideResults();
			return;
		}

		if (query === this.currentQuery) return;

		this.currentQuery = query;

		// Debounce the search
		clearTimeout(this.searchTimeout);
		this.searchTimeout = setTimeout(() => {
			this.performSearch(query);
		}, 300);
	}

	handleKeydown(event) {
		if (!this.isOpen) return;

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				this.selectNext();
				break;
			case 'ArrowUp':
				event.preventDefault();
				this.selectPrevious();
				break;
			case 'Enter':
				event.preventDefault();
				this.selectCurrent();
				break;
			case 'Escape':
				event.preventDefault();
				this.hideResults();
				this.input.blur();
				break;
		}
	}

	handleFocus() {
		if (this.currentQuery.length > 0 && this.content.children.length > 0) {
			this.showResults();
		}
	}

	handleBlur(event) {
		// Delay hiding to allow for clicks on results
		setTimeout(() => {
			if (!this.contains(document.activeElement)) {
				this.hideResults();
			}
		}, 150);
	}

	handleDocumentClick(event) {
		if (!this.contains(event.target)) {
			this.hideResults();
		}
	}

	handleSubmit(event) {
		if (this.selectedIndex >= 0 && this.items[this.selectedIndex]) {
			event.preventDefault();
			const selectedItem = this.items[this.selectedIndex];
			const link = selectedItem.querySelector('a');
			if (link) {
				window.location.href = link.href;
			}
		}
	}

	async performSearch(query) {
		if (!query || query.length < 2) {
			this.hideResults();
			return;
		}

		this.showLoading();

		try {
			const searchParams = new URLSearchParams({
				q: query,
				'resources[type]': 'product',
				'resources[limit]': '8',
				section_id: 'predictive-search'
			});

			const response = await fetch(
				`${window.routes.predictive_search_url}?${searchParams}`
			);

			if (!response.ok) {
				throw new Error('Network response was not ok');
			}

			const text = await response.text();
			this.displayResults(text, query);
		} catch (error) {
			console.error('Search error:', error);
			this.hideResults();
		}
	}

	displayResults(html, query) {
		this.hideLoading();

		// Create a temporary container to parse the HTML
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = html;

		// Debug: Log the response structure
		console.log('Predictive search response:', tempDiv);

		// Extract products from the response - look for products list specifically
		const productsList = tempDiv.querySelector(
			'#predictive-search-results-products-list'
		);
		console.log('Products list found:', productsList);

		const products = productsList
			? productsList.querySelectorAll('.predictive-search__list-item')
			: [];
		console.log('Products found:', products.length, products);

		if (products.length === 0) {
			this.showNoResults(query);
			return;
		}

		this.renderProducts(products, query);
		this.showResults();
		this.updateLiveRegion(`${products.length} products found for "${query}"`);
	}

	renderProducts(products, query) {
		const section = document.createElement('div');
		section.className = 'search-combobox__section';

		const title = document.createElement('h3');
		title.className = 'search-combobox__section-title';
		title.textContent = 'Products';
		section.appendChild(title);

		Array.from(products)
			.slice(0, 6)
			.forEach((product, index) => {
				const item = this.createProductItem(product, index);
				section.appendChild(item);
			});

		this.content.innerHTML = '';
		this.content.appendChild(section);

		this.items = this.content.querySelectorAll('.search-combobox__item');
		this.selectedIndex = -1;

		// Add click listeners to items
		this.items.forEach((item, index) => {
			item.addEventListener('click', () => {
				const link = item.querySelector('a');
				if (link) {
					window.location.href = link.href;
				}
			});

			item.addEventListener('mouseenter', () => {
				this.setSelected(index);
			});
		});
	}

	createProductItem(productElement, index) {
		const link = productElement.querySelector('a');
		const image = productElement.querySelector('img');
		const title = productElement.querySelector(
			'.predictive-search__item-heading'
		);
		const vendor = productElement.querySelector(
			'.predictive-search__item-vendor'
		);
		const price = productElement.querySelector('.price');

		const item = document.createElement('div');
		item.className = 'search-combobox__item';
		item.setAttribute('role', 'option');
		item.setAttribute('aria-selected', 'false');
		item.tabIndex = -1;

		let html = '';

		if (image) {
			html += `<img class="search-combobox__item-image" src="${
				image.src
			}" alt="${image.alt || ''}" loading="lazy">`;
		}

		html += '<div class="search-combobox__item-content">';

		if (title) {
			html += `<h4 class="search-combobox__item-title">${title.textContent}</h4>`;
		}

		if (vendor) {
			html += `<p class="search-combobox__item-vendor">${vendor.textContent}</p>`;
		}

		html += '</div>';

		if (price) {
			html += `<div class="search-combobox__item-price">${price.innerHTML}</div>`;
		}

		item.innerHTML = html;

		// Store the original link for navigation
		if (link) {
			const hiddenLink = document.createElement('a');
			hiddenLink.href = link.href;
			hiddenLink.style.display = 'none';
			item.appendChild(hiddenLink);
		}

		return item;
	}

	showLoading() {
		this.hideNoResults();
		this.content.innerHTML = '';
		this.loading.style.display = 'flex';
		this.showResults();
	}

	hideLoading() {
		this.loading.style.display = 'none';
	}

	showNoResults(query) {
		this.hideLoading();
		this.content.innerHTML = '';
		this.noResults.querySelector(
			'p'
		).textContent = `No results found for "${query}"`;
		this.noResults.hidden = false;
		this.showResults();
		this.updateLiveRegion(`No results found for "${query}"`);
	}

	hideNoResults() {
		this.noResults.hidden = true;
	}

	showResults() {
		if (!this.isOpen) {
			this.results.hidden = false;
			this.input.setAttribute('aria-expanded', 'true');
			this.isOpen = true;
		}
	}

	hideResults() {
		if (this.isOpen) {
			this.results.hidden = true;
			this.input.setAttribute('aria-expanded', 'false');
			this.hideLoading();
			this.hideNoResults();
			this.selectedIndex = -1;
			this.isOpen = false;
		}
	}

	selectNext() {
		if (this.items.length === 0) return;

		const nextIndex = this.selectedIndex + 1;
		if (nextIndex < this.items.length) {
			this.setSelected(nextIndex);
		} else {
			this.setSelected(0); // Wrap to first
		}
	}

	selectPrevious() {
		if (this.items.length === 0) return;

		const prevIndex = this.selectedIndex - 1;
		if (prevIndex >= 0) {
			this.setSelected(prevIndex);
		} else {
			this.setSelected(this.items.length - 1); // Wrap to last
		}
	}

	setSelected(index) {
		// Clear previous selection
		this.items.forEach((item, i) => {
			item.setAttribute('aria-selected', i === index ? 'true' : 'false');
		});

		this.selectedIndex = index;

		// Scroll selected item into view
		if (this.items[index]) {
			this.items[index].scrollIntoView({
				block: 'nearest',
				behavior: 'smooth'
			});
		}
	}

	selectCurrent() {
		if (this.selectedIndex >= 0 && this.items[this.selectedIndex]) {
			const selectedItem = this.items[this.selectedIndex];
			const link = selectedItem.querySelector('a');
			if (link) {
				window.location.href = link.href;
			}
		} else {
			// No item selected, submit the form normally
			this.form.submit();
		}
	}

	updateLiveRegion(message) {
		if (this.liveRegion) {
			this.liveRegion.textContent = message;
		}
	}
}

// Define the custom element
customElements.define('search-combobox', SearchCombobox);

// Initialize predictive search routes if not already defined
if (typeof window.routes === 'undefined') {
	window.routes = {};
}

if (typeof window.routes.predictive_search_url === 'undefined') {
	window.routes.predictive_search_url = '/search/suggest';
}
